// PriceUpdateManager.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Accordion } from 'react-bootstrap';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_PaginationState,
  type MRT_VisibilityState,
  type MRT_ColumnOrderState,
  type MRT_ColumnSizingState,
  type MRT_ColumnPinningState,
  type MRT_GroupingState,
  type MRT_DensityState,
} from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import FilterField, { DateFilterField, FilterTextField, PresetFilterField } from '../Trading/Filters/FilterField';
import type { FilterOptions, LabelValue } from '../../Types/filter.types';

import {
  DEFAULT_FILTERS,
  PriceUpdateFilters,
  PriceUpdateTrade,
  PriceUpdateSummary,
  STATUS_SORT_ORDER,
  DatePresetType,
  deriveSummary,
  DEFAULT_STATUS_COLOR,
  STATUS_COLORS,
  ManualPricePayload,
  TasAuditReportResponse,
  TasAuditLogRow,
} from './Priceupdatemanager.types';
import {
  fetchCommodityOptions,
  fetchPriceUpdateTrades,
  fetchPricingMechanismOptions,
  fetchTasAuditReport,
  primaryActionButtonSx,
  updateManualPrice,
  updateTasPrice,
} from './Priceupdatemanager.actions';
import { useFilterOptions } from '../Trading/PaperTrading/useFilterOptions';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';


const safeFixed = (value: unknown, digits = 2): string => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : '—';
};

const pad = (n: number) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (d: Date) => `${pad(d.getDate())}-${MONTHS[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;

const resolvePresetRange = (preset: DatePresetType): { from: string | null; to: string | null } => {
  const today = new Date();
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (preset) {
    case 'Today':
      return { from: fmt(today), to: fmt(today) };
    case 'Yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: fmt(y), to: fmt(y) };
    }
    case 'This Week': {
      const day = today.getDay() || 7;
      const monday = new Date(today);
      monday.setDate(today.getDate() - day + 1);
      return { from: fmt(start(monday)), to: fmt(today) };
    }
    case 'This Month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(first), to: fmt(today) };
    }
    case 'Previous Month': {
      const firstPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastPrev = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmt(firstPrev), to: fmt(lastPrev) };
    }
    case 'Custom':
    default:
      return { from: null, to: null };
  }
};

const cleanApiMessage = (msg: string | undefined | null): string => {
  if (!msg) return 'Could not save the price. Please try again.';
  const match = msg.match(/^\[['"](.+)['"]\]$/);
  return match ? match[1] : msg;
};


const FilterOptionsLoader: React.FC<{ onLoaded: (fo: FilterOptions) => void }> = ({ onLoaded }) => {
  const { filterOptions } = useFilterOptions();

  useEffect(() => {
    onLoaded(filterOptions);
  }, [filterOptions, onLoaded]);

  return null;
};

export interface PriceUpdateManagerProps {
  canEdit?: boolean;
}

const PriceUpdateManager: React.FC<PriceUpdateManagerProps> = ({ canEdit = true }) => {
  const [filters, setFilters] = useState<PriceUpdateFilters>(() => {
    const { from, to } = resolvePresetRange(DEFAULT_FILTERS.datePreset);
    return { ...DEFAULT_FILTERS, fromDate: from, toDate: to };
  });
  const [appliedFilters, setAppliedFilters] = useState<PriceUpdateFilters>(filters);
  const [useBaseline, setUseBaseline] = useState(true);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [rows, setRows] = useState<PriceUpdateTrade[]>([]);
  const [summary, setSummary] = useState({
  markerTasType: 'ALL',
  total: 0,
  pending: 0,
  updated: 0,
  failed: 0,
});
  const [statusChoices, setStatusChoices] = useState<string[]>(['Pending', 'Updated', 'Failed']);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pricingMechanismOptions, setPricingMechanismOptions] = useState<LabelValue[]>([]);

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({
    leg_id: true,
    process_status:true,
    status: true,
    is_TAS_MM_Updated: true,
    pricing_from: true,
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>([]);
  const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>({});
  const [columnPinning, setColumnPinning] = useState<MRT_ColumnPinningState>({});
  const [grouping, setGrouping] = useState<MRT_GroupingState>([]);
  const [density, setDensity] = useState<MRT_DensityState>('compact');

  const [isUpdatingTas, setIsUpdatingTas] = useState(false);
  const [editTrade, setEditTrade] = useState<PriceUpdateTrade | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [isSavingManualPrice, setIsSavingManualPrice] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [commodityOptions, setCommodityOptions] = useState<LabelValue[]>([]);

  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  const [isSavingBulkPrice, setIsSavingBulkPrice] = useState(false);
  const [bulkEditError, setBulkEditError] = useState<string | null>(null);

  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditReport, setAuditReport] = useState<TasAuditReportResponse | null>(null);

  const selectedTrades = useMemo(
    () => rows.filter((r) => rowSelection[r.id]),
    [rows, rowSelection],
  );

  const selectedIds = useMemo(() => selectedTrades.map((t) => t.id), [selectedTrades]);
  
  const [filterOptions, setFilterOptions] = useState<Partial<FilterOptions>>({ book: [] });
  const [hasExpandedOnce, setHasExpandedOnce] = useState(false);

  const handleFilterOptionsLoaded = useCallback((fo: FilterOptions) => {
    setFilterOptions(fo);
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const data = await fetchPriceUpdateTrades({
        filters: appliedFilters,
        useBaseline,
      });

      const sorted = [...data.info].sort(
        (a, b) => STATUS_SORT_ORDER[a.price_update_status] - STATUS_SORT_ORDER[b.price_update_status],
      );

      setRows(sorted);
      setSummary({
        markerTasType: data.grand_total?.marker_tas_type ?? 'ALL',
        total: data.grand_total?.total ?? 0,
        pending: data.grand_total?.pending ?? 0,
        updated: data.grand_total?.processed?.updated ?? 0,
        failed: data.grand_total?.processed?.not_updated ?? 0,
      });
      if (data.price_update_status_choices?.length) {
        setStatusChoices(data.price_update_status_choices);
      }

      const mechanisms = Array.from(new Set(data.info.map((r) => r.marker_tas_type))).filter(Boolean);
      if (mechanisms.length) {
        setPricingMechanismOptions((prev) => {
          const merged = new Set([...prev.map((p) => p.value), ...mechanisms]);
          return Array.from(merged).map((v) => ({ label: v, value: v }));
        });
      }
    } catch (err) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, useBaseline]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const loadPricingMechanismOptions = async () => {
      try {
        const options = await fetchPricingMechanismOptions();
        setPricingMechanismOptions(options);
      } catch (error) {
        console.error('Failed to load pricing mechanism options:', error);
        setPricingMechanismOptions([]);
      }
    };

    loadPricingMechanismOptions();
  }, []);

  useEffect(() => {
    const loadCommodityOptions = async () => {
      try {
        const options = await fetchCommodityOptions();
        setCommodityOptions(options);
      } catch (error) {
        console.error('Failed to load commodity options:', error);
        setCommodityOptions([]);
      }
    };

    loadCommodityOptions();
  }, []);

  const statusOptions: LabelValue[] = useMemo(
    () => statusChoices.map((s) => ({ label: s, value: s })),
    [statusChoices],
  );

  interface ActiveFilterChip {
    id: string;
    label: string;
    value: string;
  }

  const activeFilterChips: ActiveFilterChip[] = useMemo(() => {
    const chips: ActiveFilterChip[] = [];
    const labelFor = (opts: LabelValue[], values: string[]) =>
      values.map((v) => opts.find((o) => o.value === v)?.label ?? v).join(', ');

    if (filters.datePreset === 'Custom') {
      if (filters.fromDate || filters.toDate) {
        chips.push({ id: 'datePreset', label: 'Trade Date', value: `${filters.fromDate ?? '—'} → ${filters.toDate ?? '—'}` });
      }
    } else if (filters.datePreset !== DEFAULT_FILTERS.datePreset) {
      chips.push({ id: 'datePreset', label: 'Trade Date', value: filters.datePreset });
    }
    if (filters.pricingMechanism.length) {
      chips.push({ id: 'pricingMechanism', label: 'Pricing Mechanism', value: labelFor(pricingMechanismOptions, filters.pricingMechanism) });
    }
    if (filters.priceUpdateStatus.length) {
      chips.push({ id: 'priceUpdateStatus', label: 'Price Update Status', value: labelFor(statusOptions, filters.priceUpdateStatus) });
    }
    if (filters.symbolCode.trim()) {
      chips.push({ id: 'symbolCode', label: 'Contract / Symbol', value: filters.symbolCode.trim() });
    }
    if (filters.tradeId.trim()) {
      chips.push({ id: 'tradeId', label: 'Trade ID', value: filters.tradeId.trim() });
    }
    if (filters.commodity.length) {
      chips.push({ id: 'commodity', label: 'Commodity', value: labelFor(commodityOptions, filters.commodity) });
    }
    if (filters.book.length) {
      chips.push({ id: 'book', label: 'Book', value: labelFor(filterOptions.book ?? [], filters.book) });
    }
    return chips;
  }, [filters, pricingMechanismOptions, statusOptions,commodityOptions]);

  const hasActiveFilters = activeFilterChips.length > 0;

  const handleRemoveFilterChip = (id: string) => {
    setFilters((prev) => {
      switch (id) {
        case 'datePreset': {
          const { from, to } = resolvePresetRange(DEFAULT_FILTERS.datePreset);
          return { ...prev, datePreset: DEFAULT_FILTERS.datePreset, fromDate: from, toDate: to };
        }
        case 'pricingMechanism':
          return { ...prev, pricingMechanism: [] };
        case 'priceUpdateStatus':
          return { ...prev, priceUpdateStatus: [] };
        case 'symbolCode':
          return { ...prev, symbolCode: '' };
        case 'book':
          return { ...prev, book: [] };
        case 'tradeId':
          return { ...prev, tradeId: '' };
        case 'commodity':
          return { ...prev, commodity: [] };
        default:
          return prev;
      }
    });
  };

  const handlePresetChange = (preset: DatePresetType | null) => {
    const resolved = preset ?? 'Today';
    const { from, to } = resolvePresetRange(resolved);
    setFilters((prev: PriceUpdateFilters) => ({ ...prev, datePreset: resolved, fromDate: from, toDate: to }));
  };

  const handleApplyFilters = () => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setAppliedFilters(filters);
    setUseBaseline(false);
  };

  const handleClearFilters = () => {
    const { from, to } = resolvePresetRange(DEFAULT_FILTERS.datePreset);
    const cleared = { ...DEFAULT_FILTERS, fromDate: from, toDate: to };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setUseBaseline(true);
  };

  const handleSummaryClick = (status: 'Pending' | 'Updated' | 'Failed' | null) => {
    const next = { ...filters, priceUpdateStatus: status ? [status] : [] };
    setFilters(next);
    setAppliedFilters(next);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setUseBaseline(false);
  };

  const handleUpdateTasPrice = async () => {
    setIsUpdatingTas(true);
    try {
      const data = await updateTasPrice();
      await loadData();

      Swal.fire({
        title: data.status === 'success' ? 'TAS Prices Updated' : 'TAS Update Failed',
        icon: data.status === 'success' ? 'success' : 'error',
        width: 380,
        confirmButtonColor: '#585bcc',
        html: `
          <div style="text-align:left;font-size:0.85rem;line-height:1.7">
            <div>Total TAS: <strong>${data['Total TAS']}</strong></div>
            <div>Pending TAS: <strong>${data['Pending TAS']}</strong></div>
            <div>Processed TAS: <strong>${data['Processed TAS']}</strong></div>
            <div>Total TAS Updated: <strong>${data['Total TAS Updated']}</strong></div>
            <div>Flag Set True: <strong>${data.flag_set_true}</strong></div>
            <div>Flag Set False: <strong>${data.flag_set_false}</strong></div>
            <div>No API Match: <strong>${data.no_api_match}</strong></div>
          </div>
        `,
      });
    } catch (err) {
      setIsError(true);
      Swal.fire({
        title: 'TAS Update Failed',
        text: 'Could not run the TAS price update. Please try again.',
        icon: 'error',
        width: 380,
        confirmButtonColor: '#585bcc',
      });
    } finally {
      setIsUpdatingTas(false);
    }
  };

  const openEditModal = (trade: PriceUpdateTrade) => {
    setEditError(null);
    setEditPrice(trade.paper_price != null ? String(trade.paper_price) : '');
    setEditTrade(trade);
  };

  const closeEditModal = () => {
    setEditTrade(null);
    setEditError(null);
  };

  const handleSaveManualPrice = async () => {
    if (!editTrade) return;
    const price = Number(editPrice);
    if (!editPrice || Number.isNaN(price) || price <= 0) {
      setEditError('Enter a valid price.');
      return;
    }
    setIsSavingManualPrice(true);
    try {
      const data = await updateManualPrice({ id: editTrade.id, price });

      const result = data.results?.[0];
      const failed =
        !data.status ||
        (data.summary && (!data.summary.updated || result?.status !== 'UPDATED'));

      if (failed) {
        const msg = cleanApiMessage(result?.message ?? data.message);
        toast.error(msg);
        setEditError(msg);
        return;
      }

      toast.success(cleanApiMessage(data.message) || 'Price updated successfully.');
      closeEditModal();
      await loadData();
    } catch (err) {
      const msg = 'Could not save the price. Please try again.';
      toast.error(msg);
      setEditError(msg);
    } finally {
      setIsSavingManualPrice(false);
    }
  };

  const openBulkEditModal = () => {
    if (!selectedTrades.length) {
      Swal.fire({
        title: 'No Trades Selected',
        text: 'Please select at least one trade.',
        icon: 'warning',
        width: 380,
        confirmButtonColor: '#585bcc',
      });
      return;
    }

    const commodities = new Set(selectedTrades.map((t) => t.commodity));
    const pricingFroms = new Set(selectedTrades.map((t) => t.pricing_from));
    const mechanisms = new Set(selectedTrades.map((t) => t.marker_tas_type));

    if (commodities.size > 1 || pricingFroms.size > 1 || mechanisms.size > 1) {
      Swal.fire({
        title: 'Cannot Bulk Edit',
        icon: 'warning',
        width: 420,
        confirmButtonColor: '#585bcc',
        html: `
        <div style="text-align:left;font-size:0.85rem;line-height:1.7">
          Selected trades must have the same Commodity, Pricing From, and Pricing Mechanism.
          <div style="margin-top:8px">
            <div>Commodity: <strong>${commodities.size}</strong> distinct value(s)</div>
            <div>Pricing From: <strong>${pricingFroms.size}</strong> distinct value(s)</div>
            <div>Pricing Mechanism: <strong>${mechanisms.size}</strong> distinct value(s)</div>
          </div>
        </div>
      `,
      });
      return;
    }

    setBulkEditError(null);
    setBulkPrice('');
    setIsBulkEditOpen(true);
  };
  const closeBulkEditModal = () => {
    setIsBulkEditOpen(false);
    setBulkEditError(null);
  };

  const handleSaveBulkPrice = async () => {
    const price = Number(bulkPrice);
    if (!bulkPrice || Number.isNaN(price) || price <= 0) {
      setBulkEditError('Enter a valid price.');
      return;
    }
    if (!selectedIds.length) {
      setBulkEditError('No trades selected.');
      return;
    }

    const payload: ManualPricePayload[] = selectedIds.map((id) => ({ id, price }));

    setIsSavingBulkPrice(true);
    try {
      const data = await updateManualPrice(payload);
      const results = data.results ?? [];
      const failed = results.filter((r) => r.status !== 'UPDATED');

      if (failed.length) {
        const msg = cleanApiMessage(failed[0]?.message ?? data.message);
        toast.error(`${failed.length}/${selectedIds.length} failed: ${msg}`);
        setBulkEditError(msg);
        return;
      }

      toast.success(cleanApiMessage(data.message) || 'Prices updated successfully.');
      closeBulkEditModal();
      setRowSelection({});
      await loadData();
    } catch (err) {
      const msg = 'Could not save prices. Please try again.';
      toast.error(msg);
      setBulkEditError(msg);
    } finally {
      setIsSavingBulkPrice(false);
    }
  };
  
  const openAuditReport = async () => {
    setIsAuditOpen(true);
    setIsAuditLoading(true);
    setAuditError(null);
    try {
      const report = await fetchTasAuditReport();
      setAuditReport(report);
    } catch (err) {
      setAuditError('Could not load TAS audit report.');
      setAuditReport(null);
    } finally {
      setIsAuditLoading(false);
    }
  };

  const closeAuditReport = () => {
    setIsAuditOpen(false);
  };

  const auditSummaryItems = useMemo(() => {
    if (!auditReport?.summary?.length) return [];
    return auditReport.summary.map((s) => {
      const idx = s.indexOf(':');
      return idx === -1
        ? { label: s, value: '' }
        : { label: s.slice(0, idx).trim(), value: s.slice(idx + 1).trim() };
    });
  }, [auditReport]);

  const auditRows = auditReport?.data ?? [];


  const auditTableColumns = useMemo<MRT_ColumnDef<TasAuditLogRow>[]>(() => {
    if (!auditReport?.columns?.length) return [];
    return auditReport.columns.map((col) => ({
      accessorKey: col as keyof TasAuditLogRow,
      header: col,
      size: 140,
      Cell: ({ cell }) => {
        const v = cell.getValue<string>();
        return v === '' || v == null ? '—' : v;
      },
    }));
  }, [auditReport]);


  const auditTable = useMaterialReactTable({
    columns: auditTableColumns,
    data: auditRows,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableDensityToggle: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    initialState: { density: 'compact' },
    muiTableContainerProps: { sx: { maxHeight: 480 } },
    state: { isLoading: isAuditLoading },
  });


  const columns = useMemo<MRT_ColumnDef<PriceUpdateTrade>[]>(
    () => [
      { accessorKey: 'trade_date', header: 'Trade Date', size: 140 },
      {
        accessorKey: 'price_update_status',
        header: 'Price Update Status',
        size: 170,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>() || 'N/A';
          const normalized = value.toLowerCase();
          const palette = STATUS_COLORS[normalized] ?? DEFAULT_STATUS_COLOR;
          const displayValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

          return (
            <Chip
              label={displayValue}
              size="small"
              sx={{
                backgroundColor: palette.bg,
                color: palette.color,
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
                letterSpacing: 0.3,
                textTransform: 'capitalize',
              }}
            />
          );
        },
      },
      { accessorKey: 'marker_tas_type', header: 'Pricing Mechanism', size: 170 },
      { accessorKey: 'book', header: 'Book', size: 120 },
      { accessorKey: 'commodity', header: 'Commodity', size: 200 },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        size: 130,
        Cell: ({ cell }) => safeFixed(cell.getValue()),
      },
      { accessorKey: 'pricing_from', header: 'Pricing From', size: 160 },
      {
        accessorKey: 'paper_price',
        header: 'Price',
        size: 150,
        Cell: ({ cell }) => safeFixed(cell.getValue()),
      },
      {
        accessorKey: 'mtm',
        header: 'MTM',
        size: 130,
        Cell: ({ cell }) => safeFixed(cell.getValue()),
      },
       {
        accessorKey: 'api_price',
        header: 'EXCH Diff Price',
        size: 140,
        Cell: ({ cell }) => safeFixed(cell.getValue()),
      },
      {
        accessorKey: 'expected_price',
        header: 'Expected Price',
        size: 160,
        Cell: ({ cell }) => safeFixed(cell.getValue()),
      },
      { accessorKey: 'trade_id', header: 'Trade ID', size: 220 },
      { accessorKey: 'symbol_code', header: 'Symbol', size: 120 },
      { accessorKey: 'status', header: 'Status', size: 160 },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    getRowId: (row) => row.id,

    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableRowActions: true,
    enableKeyboardShortcuts: true,
    enableRowNumbers: false,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableFullScreenToggle: true,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableColumnFilters: false,
    enableColumnFilterModes: false,
    enableRowSelection: true,
    enableRowVirtualization: false,
    enableColumnResizing: true,
    defaultColumn: { minSize: 100 },
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    enableColumnOrdering: true,
    enableRowOrdering: false,
    enableEditing: false,

    manualSorting: false,
    manualPagination: false,
    manualFiltering: true,

    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnPinningChange: setColumnPinning,
    onGroupingChange: setGrouping,
    onDensityChange: setDensity,

    initialState: {
      showColumnFilters: false,
      columnOrder: columns.map((c) => (c as any).accessorKey ?? c.id ?? ''),
    },

    muiTableBodyCellProps: {
      sx: {
        fontSize: '0.65rem',
        backgroundColor: 'transparent !important',
      },
    },
    muiTopToolbarProps: {
      sx: { backgroundColor: '#fff', padding: '8px' },
      className: 'top-toolbar-wrapper__new',
    },
    muiTableContainerProps: ({ table: t }) => ({
      sx: { backgroundColor: '#fff' },
      className: `table-container-wrapper__new ${t.getState().isFullScreen ? 'maximized' : ''}`,
    }),
    muiTableFooterProps: { sx: { backgroundColor: '#fff' }, className: 'table-footer-wrapper__new' },
    muiBottomToolbarProps: { sx: { backgroundColor: '#fff', padding: '8px' }, className: 'bottom-toolbar-wrapper__new' },
    muiTableHeadCellProps: {
      sx: {
        padding: '8px 10px',
        fontSize: '0.7rem',
        textAlign: 'center',
        verticalAlign: 'bottom',
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'center',
        },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          whiteSpace: 'normal',
          overflow: 'visible',
          textOverflow: 'unset',
          wordBreak: 'break-word',
          lineHeight: 1.25,
        },
        '& .Mui-TableHeadCell-ResizeHandle': {
          backgroundColor: '#ddd',
          width: '3px',
          borderRadius: '2px',
          '&:hover': { backgroundColor: '#585bcc' },
        },
      },
    },
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: '0', border: '0' } },
    muiToolbarAlertBannerProps: isError ? { color: 'error', children: 'Error loading data' } : undefined,

    renderRowActions: ({ row }) => {
      const trade = row.original;
      return (
        <Box sx={{ display: 'flex' }}>
          <Tooltip title="Edit Price">
            <IconButton disabled={!canEdit} onClick={() => openEditModal(trade)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': { size: 90 },
      'mrt-row-select': { size: 50 },
    },

    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button size="small" onClick={loadData} startIcon={<RefreshIcon />}>
          Refresh
        </Button>
        <Button size="small" onClick={handleClearFilters}>
          Reset
        </Button>
        <Tooltip
          title={
            canEdit
              ? 'Runs the automatic update for all pending TAS trades'
              : 'View-only access cannot run updates'
          }
        >
          <span>
            <Button
              size="small"
              variant="contained"
              sx={primaryActionButtonSx}
              disabled={!canEdit || isUpdatingTas}
              onClick={handleUpdateTasPrice}
              startIcon={isUpdatingTas ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
            >
              {isUpdatingTas ? 'Updating...' : 'Update TAS Price'}
            </Button>
          </span>
        </Tooltip>
        <span>
          <Button
            variant="contained"
            onClick={openAuditReport}
            disabled={isAuditLoading}
            sx={primaryActionButtonSx}
            startIcon={isAuditLoading ? <CircularProgress size={14} sx={{ color: '#ffff' }} /> : undefined}
          >
            {isAuditLoading ? 'Loading...' : 'TAS Audit Log'}
          </Button>
        </span>

        {canEdit && selectedIds.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={openBulkEditModal}
            startIcon={<EditIcon fontSize="small" />}
          >
            Edit Selected ({selectedIds.length})
          </Button>
        )}
      </Box>
    ),

    state: {
      columnVisibility,
      sorting,
      pagination,
      rowSelection,
      columnOrder,
      columnSizing,
      columnPinning,
      grouping,
      density,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isLoading,
    },
  });

  return (
    <div className="page-body">
      <div className="table-ad">
        <Accordion defaultActiveKey="0" className="table-ad__addInputs">
          <Accordion.Item eventKey="0">
            <Accordion.Header className="accordion_button">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <span>Price Update Manager</span>
              </div>
            </Accordion.Header>

            <Accordion.Body>
              {isError && (
                <div
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.8rem',
                    marginBottom: 8,
                  }}
                >
                  Error loading data. Please try Refresh.
                </div>
              )}

              <Paper
                elevation={1}
                sx={{ mb: 1.5, overflow: 'hidden', border: '1px solid #e0e0e0' }}
              >
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.65,
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#eeeeee' },
                  }}
                  onClick={() => {
                    setIsFilterExpanded((prev) => !prev);
                    setHasExpandedOnce(true);
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FilterListIcon fontSize="small" sx={{ color: hasActiveFilters ? '#000' : '#bdbdbd' }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                      {hasActiveFilters ? 'Filters (Active)' : 'Filters'}
                    </Typography>
                  </Box>
                  {isFilterExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>

                {hasActiveFilters && (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.75,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      alignItems: 'center',
                      backgroundColor: '#fff',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    {activeFilterChips.map((chip) => (
                      <Chip
                        key={chip.id}
                        label={
                          <span>
                            {chip.label}: <strong>{chip.value}</strong>
                          </span>
                        }
                        onDelete={() => handleRemoveFilterChip(chip.id)}
                        size="small"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#585bcc',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: '20px',
                          '& .MuiChip-label': { padding: '0 6px 0 2px' },
                          '& .MuiChip-deleteIcon': {
                            color: '#585bcc',
                            width: '12px',
                            height: '12px',
                            marginLeft: '2px',
                            '&:hover': { color: '#0d47a1' },
                          },
                          '&:hover': { backgroundColor: '#90caf9' },
                        }}
                      />
                    ))}
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      onClick={handleClearFilters}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#585bcc',
                        padding: '2px 8px',
                        minHeight: '24px',
                        '&:hover': { backgroundColor: 'rgba(21, 101, 192, 0.1)', color: '#0d47a1' },
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                )}

                <Collapse in={isFilterExpanded}>
                  <Box sx={{ p: 1.5 }}>
                    <Grid container spacing={1.2} alignItems="flex-start">
                      <Grid item xs={12} sm={6} md={2}>
                        <PresetFilterField
                          label="Trade Date"
                          value={filters.datePreset}
                          onChange={handlePresetChange}
                        />
                      </Grid>

                      {filters.datePreset === 'Custom' && (
                        <>
                          <Grid item xs={12} sm={6} md={2}>
                            <DateFilterField
                              label="Trade Date Start"
                              value={filters.fromDate ?? ''}
                              onChange={(v) => setFilters((p: PriceUpdateFilters) => ({ ...p, fromDate: v }))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={2}>
                            <DateFilterField
                              label="Trade Date End"
                              value={filters.toDate ?? ''}
                              onChange={(v) => setFilters((p: PriceUpdateFilters) => ({ ...p, toDate: v }))}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} sm={6} md={2}>
                        <FilterField
                          label="Pricing Mechanism"
                          field="pricingMechanism"
                          options={pricingMechanismOptions}
                          value={{ values: filters.pricingMechanism, isAll: false }}
                          onChange={(_field, values) =>
                            setFilters((p: PriceUpdateFilters) => ({ ...p, pricingMechanism: values }))
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <FilterField
                          label="Price Update Status"
                          field="priceUpdateStatus"
                          options={statusOptions}
                          value={{ values: filters.priceUpdateStatus, isAll: false }}
                          onChange={(_field, values) => setFilters((p: PriceUpdateFilters) => ({ ...p, priceUpdateStatus: values }))}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <FilterTextField
                          label="Trade ID"
                          value={filters.tradeId}
                          onChange={(v) => setFilters((p: PriceUpdateFilters) => ({ ...p, tradeId: v }))}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <FilterField
                          label="Book"
                          field="book"
                          options={filterOptions.book ?? []}
                          value={{ values: filters.book, isAll: false }}
                          onChange={(_field, values) => setFilters((p: PriceUpdateFilters) => ({ ...p, book: values }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <FilterField
                          label="Commodity"
                          field="commodity"
                          options={commodityOptions}
                          value={{ values: filters.commodity, isAll: false }}
                          onChange={(_field, values) => setFilters((p: PriceUpdateFilters) => ({ ...p, commodity: values }))}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md="auto">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-start' }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleApplyFilters}
                            sx={{
                              minWidth: { xs: 'auto', sm: '100px' },
                              height: '32px',
                              backgroundColor: '#00bcd4',
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              '&:hover': { backgroundColor: '#008c9e' },
                            }}
                          >
                            Apply
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleClearFilters}
                            disabled={!hasActiveFilters}
                            sx={{
                              minWidth: { xs: 'auto', sm: '100px' },
                              height: '32px',
                              backgroundColor: '#f44336',
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              '&:hover': { backgroundColor: '#ba000d' },
                              '&:disabled': { backgroundColor: '#ccc' },
                            }}
                          >
                            Clear All
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>

              <Stack direction="row" spacing={1} sx={{ my: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: '8px',
                    border: '1px solid #eaeaea',
                    backgroundColor: '#fff',
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#616161', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.7rem', color: '#8a8a8a', fontWeight: 600, letterSpacing: '0.02em' }}>
                    Marker TAS Type
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#212121', fontWeight: 700 }}>
                    {summary.markerTasType}
                  </Typography>
                </Box>
                {[
                  { label: 'Total', value: summary.total, status: null as null, color: '#585bcc' },
                  { label: 'Updated', value: summary.updated, status: 'Updated' as const, color: '#2E7D32' },
                  { label: 'Pending', value: summary.pending, status: 'Pending' as const, color: '#ff4e00' },
                  { label: 'Failed', value: summary.failed, status: 'Failed' as const, color: '#C62828' },
                ].map((s) => (
                  <Box
                    key={s.label}
                    onClick={() => handleSummaryClick(s.status)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: '8px',
                      border: '1px solid #eaeaea',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: s.color,
                        backgroundColor: `${s.color}0d`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: s.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ fontSize: '0.7rem', color: '#8a8a8a', fontWeight: 600, letterSpacing: '0.02em' }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#212121', fontWeight: 700 }}>
                      {s.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <div className="table-ad__wrapper typeCtapitalize">
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>

      <Dialog
        open={!!editTrade}
        onClose={closeEditModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '10px', overflow: 'hidden' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            backgroundColor: '#fff',
            color: '#212121',
            borderBottom: '1px solid #eee',
          }}
        >
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Edit Price</Typography>
          <IconButton size="small" onClick={closeEditModal}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            {[
              { label: 'Contract', value: editTrade?.commodity ?? '—' },
              { label: 'Trade Date', value: editTrade?.trade_date ?? '—' },
              { label: 'Book', value: editTrade?.book ?? '—' },
              { label: 'Pricing Mechanism', value: editTrade?.marker_tas_type ?? '—' },
              { label: 'Current Price', value: safeFixed(editTrade?.paper_price) },
            ].map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.6,
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: '#757575', fontWeight: 500 }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#212121', fontWeight: 600 }}>
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#757575', fontWeight: 600, mb: 0.5 }}>
              Price
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="number"
              placeholder="Enter Price"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              error={!!editError}
              helperText={editError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            disabled={isSavingManualPrice}
            onClick={handleSaveManualPrice}
            sx={{
              backgroundColor: '#585bcc',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              borderRadius: '6px',
              px: 2.5,
              '&:hover': { backgroundColor: '#4547a3' },
            }}
          >
            {isSavingManualPrice ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      {hasExpandedOnce && <FilterOptionsLoader onLoaded={handleFilterOptionsLoaded} />}
      <Dialog
        open={isBulkEditOpen}
        onClose={closeBulkEditModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            backgroundColor: '#fff',
            color: '#212121',
            borderBottom: '1px solid #eee',
          }}
        >
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
            Edit Selected ({selectedIds.length} trades)
          </Typography>
          <IconButton size="small" onClick={closeBulkEditModal}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            {[
              { label: 'Commodity', value: selectedTrades[0]?.commodity ?? '—' },
              { label: 'Pricing From', value: selectedTrades[0]?.pricing_from ?? '—' },
              { label: 'Pricing Mechanism', value: selectedTrades[0]?.marker_tas_type ?? '—' },
              { label: 'Trades Selected', value: String(selectedIds.length) },
            ].map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.6,
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: '#757575', fontWeight: 500 }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#212121', fontWeight: 600 }}>
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#757575', fontWeight: 600, mb: 0.5 }}>
              Price (applied to all selected trades)
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="number"
              placeholder="Enter Price"
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              error={!!bulkEditError}
              helperText={bulkEditError}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', borderRadius: '6px' } }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            disabled={isSavingBulkPrice || selectedIds.length === 0}
            onClick={handleSaveBulkPrice}
            sx={{
              backgroundColor: '#585bcc',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              borderRadius: '6px',
              px: 2.5,
              '&:hover': { backgroundColor: '#4547a3' },
            }}
          >
            {isSavingBulkPrice ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isAuditOpen}
        onClose={closeAuditReport}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            backgroundColor: '#fff',
            color: '#212121',
            borderBottom: '1px solid #eee',
          }}
        >
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>TAS Audit Log</Typography>
          <IconButton size="small" onClick={closeAuditReport}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
          {isAuditLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!isAuditLoading && auditError && (
            <Typography sx={{ fontSize: '0.8rem', color: '#d32f2f' }}>{auditError}</Typography>
          )}

          {!isAuditLoading && !auditError && auditReport && (
            <>
              {!!auditReport.date && (
                <Typography sx={{ fontSize: '0.75rem', color: '#757575', mb: 1 }}>
                  As of {auditReport.date}
                </Typography>
              )}

              {!!auditSummaryItems.length && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                  {auditSummaryItems.map((item) => (
                    <Chip
                      key={item.label}
                      label={`${item.label}: ${item.value}`}
                      size="small"
                      sx={{ fontSize: '0.65rem', fontWeight: 600, backgroundColor: '#f0f0f0' }}
                    />
                  ))}
                </Box>
              )}

              {auditRows.length === 0 ? (
                <Typography sx={{ fontSize: '0.8rem', color: '#757575' }}>No audit records found.</Typography>
              ) : (
                <MaterialReactTable table={auditTable} />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceUpdateManager;